import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import Image from '../../image'
import { extractUrlSettings } from '../../../../utils/cloudinary'
import settings from '../../settings'

const ImageWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  box-sizing: content-box;
  overflow: hidden;
  justify-content: space-between;
  justify-content: ${theme('imageOption.justifyContent')};
  min-width: ${theme('imageOption.desktop.minWidth')};
  height: 100%;
  box-sizing: border-box;
  background-color: ${(props) =>
    theme('imageOption.imageForColor.backgroundColor')(props) ||
    theme('textOption.backgroundColor')(props)};

  ${(props) => props.theme.media.min.sm`
    width: ${theme('imageOption.wrap.desktop.width')};
  `}
`

const ImageContainer = styled.div`
  display: block;
`

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${theme(
    'imageOption.imageForColor.imageWrapper.backgroundColor'
  )};
  border-radius: ${theme(
    'imageOption.imageForColor.imageWrapper.borderRadius'
  )};
  border: ${(props) =>
    props.$active
      ? theme('imageOption.imageForColor.imageWrapper.borderActive')
      : theme('imageOption.imageForColor.imageWrapper.border')};
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.15);
`

const StyledImage = styled(Image)`
  width: ${(p) => {
    if (settings.brand === 'magnet') {
      return theme('imageOption.imageForColor.width')
    }
    return theme(`quickQuestions.image.${p.questionStyle}.width`)
  }};
  height: ${(p) => {
    if (settings.brand === 'magnet') {
      return theme('imageOption.imageForColor.height')
    }
    return theme(`quickQuestions.image.${p.questionStyle}.height`)
  }};
  margin: ${theme('imageOption.imageForColor.margin')};
  border-radius: ${theme('imageOption.imageForColor.borderRadius')};
  @media (max-width: 428px) {
    width: ${theme('imageOption.imageForColor.mobile.width')};
    height: ${theme('imageOption.imageForColor.mobile.height')};
    margin: ${theme('imageOption.imageForColor.mobile.margin')};
  }
`

const Text = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  min-height: ${(props) =>
    theme('imageOption.text.minHeight')(props) || '40px'};
  line-height: ${theme('imageOption.text.lineHeight')};
  min-width: ${settings.brand === 'magnet' ? '90px' : '120px'};
  margin-top: ${theme('imageOption.text.marginTop')};
  margin-bottom: ${theme('imageOption.text.marginBottom')};
  font-family: ${theme('textOption.text.fontFamily')};
  font-size: ${settings.brand === 'magnet'
    ? '14px'
    : theme('textOption.text.fontSize.mobile')};
  font-weight: ${(props) => {
    if (settings.brand === 'magnet') {
      return props.$active
        ? theme('imageOption.text.fontWeight')
        : theme('imageOption.text.fontWeight')
    }
    return props.$active
      ? theme('textOption.text.active.fontWeight')
      : theme('textOption.text.fontWeight')
  }};

  color: ${theme('colors.link')};
  white-space: pre-wrap;
  white-space: ${theme('textOption.text.whiteSpace')};
  text-align: center;
  text-align: ${theme('textOption.text.textAlign')};
  font-style: ${theme('textOption.text.fontStyle')};

  em {
    font-weight: 400;
  }

  p {
    margin-top: 0;
    margin-bottom: 0;
  }

  ${(props) => props.theme.media.min.sm`
    min-width: 120px;
    font-size: ${theme(
      'imageOption.text.fontSize.desktop',
      theme('textOption.text.fontSize.desktop')
    )(props)};
  `}
`

const ImageOption = ({ name, image, text, active, questionStyle }) => {
  const imageUrl = useMemo(() => {
    if (!image || !image.url) {
      return null
    }
    const url = image.url
    const publicId = image.publicId
    if (!publicId) {
      return url
    }

    let type = image.type
    if (!type) {
      const { deliveryType } = extractUrlSettings(url)
      type = deliveryType
    }

    return `https://assets.nobiadigital.com/image/${type}/c_scale,w_480/q_auto/f_auto/${publicId}`
  }, [image])

  return (
    <ImageWrap>
      {image && (
        <ImageContainer>
          <ImageWrapper $active={active}>
            {image && (
              <StyledImage
                value={{ url: imageUrl, alt: name }}
                sizes="40px"
                questionStyle={questionStyle}
              />
            )}
          </ImageWrapper>
        </ImageContainer>
      )}
      {text && (
        <Text $active={active} dangerouslySetInnerHTML={{ __html: text }} />
      )}
    </ImageWrap>
  )
}

ImageOption.defaultProps = {
  text: null,
  image: null,
  active: null,
  questionStyle: 'style1',
}

ImageOption.propTypes = {
  name: PropTypes.string.isRequired,
  text: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string,
    publicId: PropTypes.string,
    type: PropTypes.string,
  }),
  active: PropTypes.bool.isRequired,
  questionStyle: PropTypes.string,
}

export default ImageOption
